import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  FiAward,
  FiCheckCircle,
  FiTrendingUp,
  FiUsers,
  FiShield,
  FiGift,
  FiArrowRight,
  FiCheck,
} from "react-icons/fi";
import { becomeBrandAmbassador } from "../../../api/customer/customerFeatureApis";
import { loginSuccess } from "../../../features/auth/authSlice";

const BrandAmbassadorHub = () => {
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;

  const user = loggedInUser?.userDetails || loggedInUser?.user || loggedInUser;
  const isAlreadyAmbassador = user?.is_brand_ambassador === true;

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleEnroll = async () => {
    setLoading(true);
    setErrorMessage(null);

    try {
      const res = await becomeBrandAmbassador(token, user);
      if (res?.statusType === "SUCCESS" || res?.text) {
        // Update user in localStorage
        const stored = localStorage.getItem("user");
        let updatedUser = { ...loggedInUser };
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            parsed.is_brand_ambassador = true;
            if (parsed.userDetails) parsed.userDetails.is_brand_ambassador = true;
            localStorage.setItem("user", JSON.stringify(parsed));
            updatedUser = parsed;
          } catch (e) {}
        }

        if (dispatch) {
          dispatch(
            loginSuccess({
              user: {
                ...updatedUser,
                is_brand_ambassador: true,
                userDetails: {
                  ...(updatedUser.userDetails || {}),
                  is_brand_ambassador: true,
                },
              },
            })
          );
        }

        setSuccessMessage("Congratulations! You are officially an All Home Desire Brand Ambassador.");
      } else {
        setErrorMessage(res?.text || "Unable to activate Brand Ambassador status. Please try again.");
      }
    } catch (err) {
      setErrorMessage(err?.response?.data?.text || "Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const perks = [
    {
      icon: FiGift,
      title: "Earn Extra Referral Rewards",
      desc: "Receive enhanced points whenever referred friends, family, or partner professionals register and list on All Home Desire.",
    },
    {
      icon: FiAward,
      title: "Exclusive Verified Profile Badge",
      desc: "Stand out across community discussions and member lists with the official All Home Desire Brand Ambassador distinction.",
    },
    {
      icon: FiTrendingUp,
      title: "Early Access to Features & Deals",
      desc: "Be the first to preview exclusive promotional properties, newly onboarded local vendors, and premium marketplace deals.",
    },
    {
      icon: FiUsers,
      title: "Direct Network Community Leader",
      desc: "Organize regional neighborhood community groups and connect buyers, tenants, and verified home service providers.",
    },
  ];

  return (
    <div className="d-flex flex-column gap-4" style={{ maxWidth: "900px", margin: "0 auto" }}>
      {/* Hero Banner */}
      <div
        className="rounded-4 p-4 p-md-5 text-white position-relative overflow-hidden shadow-sm"
        style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)",
        }}
      >
        <div className="position-relative" style={{ zIndex: 1, maxWidth: "620px" }}>
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill bg-white/10 text-amber-300 mb-3 border border-warning">
            <FiAward size={16} />
            <span className="small fw-bold">Official Ambassador Program</span>
          </div>

          <h1 className="fw-bold mb-3 fs-2 text-white">
            {isAlreadyAmbassador
              ? "You Are an All Home Desire Brand Ambassador"
              : "Become an All Home Desire Brand Ambassador"}
          </h1>

          <p className="text-light mb-4 opacity-90 lh-base">
            {isAlreadyAmbassador
              ? "Thank you for representing All Home Desire in your community. Your verified ambassador badge is active, and your referral bonuses are unlocked."
              : "Join our network of community champions. Share trusted home services and verified properties while earning exclusive platform points, bonuses, and regional recognition."}
          </p>

          {isAlreadyAmbassador ? (
            <div className="d-inline-flex align-items-center gap-2 bg-emerald-900 bg-opacity-75 text-emerald-200 px-4 py-2 rounded-3 border border-emerald-500">
              <FiCheckCircle size={20} className="text-success" />
              <span className="fw-semibold">Status: Active Ambassador</span>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-warning btn-lg px-4 py-3 fw-bold d-inline-flex align-items-center gap-2 shadow"
              onClick={handleEnroll}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span>Activating...</span>
                </>
              ) : (
                <>
                  <span>Enroll as Brand Ambassador</span>
                  <FiArrowRight size={18} />
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Success Notification */}
      {successMessage && (
        <div className="alert alert-success d-flex align-items-center gap-2 p-3 rounded-3 shadow-sm" role="alert">
          <FiCheckCircle size={20} className="text-success flex-shrink-0" />
          <span className="fw-medium">{successMessage}</span>
        </div>
      )}

      {/* Error Notification */}
      {errorMessage && (
        <div className="alert alert-danger d-flex align-items-center gap-2 p-3 rounded-3 shadow-sm" role="alert">
          <FiShield size={20} className="text-danger flex-shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Perks Grid */}
      <div>
        <h2 className="fs-5 fw-bold text-dark mb-3">Ambassador Privileges & Impact</h2>
        <div className="row g-3">
          {perks.map((perk, index) => {
            const Icon = perk.icon;
            return (
              <div key={index} className="col-12 col-md-6">
                <div className="p-4 bg-white rounded-3 border border-slate-200 h-100 shadow-sm d-flex flex-column gap-2">
                  <div
                    className="d-inline-flex align-items-center justify-content-center rounded-3 bg-blue-50 text-primary mb-1"
                    style={{ width: "44px", height: "44px" }}
                  >
                    <Icon size={22} />
                  </div>
                  <h3 className="fs-6 fw-bold text-dark mb-1">{perk.title}</h3>
                  <p className="text-secondary small mb-0 lh-base">{perk.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Ambassador Code of Quality */}
      <div className="p-4 bg-light rounded-3 border border-slate-200">
        <h3 className="fs-6 fw-bold text-dark mb-2">Our Quality Standard</h3>
        <ul className="list-unstyled mb-0 d-flex flex-column gap-2 small text-secondary">
          <li className="d-flex align-items-start gap-2">
            <FiCheck className="text-success mt-1 flex-shrink-0" />
            <span>Always recommend verified property listings and reputable service vendors.</span>
          </li>
          <li className="d-flex align-items-start gap-2">
            <FiCheck className="text-success mt-1 flex-shrink-0" />
            <span>Foster respectful, helpful collaboration in community discussions and reviews.</span>
          </li>
          <li className="d-flex align-items-start gap-2">
            <FiCheck className="text-success mt-1 flex-shrink-0" />
            <span>Never share deceptive advertisements, spam links, or inaccurate pricing details.</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default BrandAmbassadorHub;
